import type { CSSProp } from 'styled-components';
import styled, { css } from 'styled-components';

import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import type { TypographyProps } from '@/components/Typography';
import { Typography } from '@/components/Typography';
import { textUnderlineOnHover } from '@/theme';
import { theme } from '@/theme/getTheme';
import type { Colors, Space } from '@/theme/types';
import type {
  filledButtonStylesProps,
  linkButtonStylesProps,
  outlinedButtonStylesProps,
} from '@/theme/types/components';

import type { ButtonProps } from './LegacyButton';

export const defaultProps: Required<
  Pick<
    ButtonProps,
    'as' | 'variant' | 'colorVariant' | 'size' | 'beforeContent'
  >
> = {
  as: 'button',
  variant: 'filled',
  colorVariant: 'primary',
  size: 'm',
  beforeContent: false,
};

const paddingProps = {
  s: (space: Space) => space.s200,
  m: (space: Space) => space.s300,
  l: () => theme.spacing(11),
};

const getPaddingProps = (size = defaultProps.size, space: Space) =>
  paddingProps[size](space);

export const switchTypographyProps = (
  size = defaultProps.size,
): Pick<TypographyProps, 'px' | 'scale'> => {
  switch (size) {
    case 's':
      return { px: 's200', scale: 'p2' };
    case 'm':
      return { px: 's200', scale: 'p2' };
    default:
      return { px: 's000' };
  }
};

export const switchSize = (size = defaultProps.size, space: Space) => {
  switch (size) {
    case 's':
      return space.s250;
    case 'm':
      return space.s300;
    default:
      return 'auto';
  }
};

const filledStyles = ({
  beforeContent,
  bg,
  color,
  disabledColor,
  hoverBg,
  hoverBorderColor,
  hoverColor,
  isDisabled,
  isProcessing,
}: filledButtonStylesProps) => {
  return css`
    background-color: ${bg};
    color: ${color};
    border: 2px solid ${bg};

    ${!isDisabled &&
    !isProcessing &&
    css`
      &:hover {
        color: ${hoverColor};
        background-color: ${hoverBg};
        border: 2px solid ${hoverBorderColor};
      }
    `}

    &:hover:disabled {
      color: ${disabledColor};
    }
    ${beforeContent &&
    css`
      &::before {
        background-color: ${bg};
      }
    `}
  `;
};

const outlinedStyles = ({
  color,
  disabledBg,
  disabledBorderColor,
  disabledColor,
  hoverBg,
  hoverBorderColor,
  hoverColor,
}: outlinedButtonStylesProps) => {
  return css`
    color: ${color};
    background-color: transparent;
    transition: ${theme.transitions.create('all', {
      easing: 'easeInOut',
      duration: 0.3,
    })};

    &:focus,
    &:hover {
      background-color: ${hoverBg};
      border-color: ${hoverBorderColor};
      color: ${hoverColor};
    }
    &:disabled {
      background-color: ${disabledBg ?? 'transparent'};
      border-color: ${disabledBorderColor ?? theme.colors.secondary};
      color: ${disabledColor ?? theme.colors.secondary};
    }
  `;
};

const linkStyles = ({
  color,
  disabledColor,
  hoverColor,
  iconColor,
}: linkButtonStylesProps) => {
  return css`
    color: ${iconColor ?? color}; /* The button icon color */
    padding-left: 0;
    padding-right: 0;
    text-decoration: none;
    &:focus,
    > span {
      color: ${color}; /* The button label text color */
    }
    &:hover {
      color: ${hoverColor};
      > span {
        color: ${hoverColor};
      }
    }
    &:disabled {
      color: ${disabledColor};
      opacity: 0.65;
    }
    ${textUnderlineOnHover};
  `;
};

export const switchStyles = (
  variant = defaultProps.variant,
  colorVariant = defaultProps.colorVariant,
  colors: Colors,
  space: Space,
  borderWidth: string,
  size = defaultProps.size,
  beforeContent = false,
  isDisabled = false,
  isProcessing = false,
): CSSProp => {
  const defaultDisabledStyles = css`
    &:disabled {
      opacity: 0.65;

      &:hover {
        cursor: not-allowed;
      }
    }
  `;

  const outlinedButton = css`
    border: ${borderWidth} solid currentColor;
    background-color: transparent;
    padding-block: 0;
    padding-inline: calc(${getPaddingProps(size, space)} - ${borderWidth});
  `;

  const underlinedButton = css`
    background-color: transparent;
    padding-block: 0;
    padding-inline: calc(${getPaddingProps(size, space)} - ${borderWidth});
  `;

  let colorStyles: CSSProp = '';
  const params = {
    ...theme.components.button.colorVariant[colorVariant][variant],
    isDisabled,
    isProcessing,
  };

  switch (variant) {
    case 'filled':
      colorStyles = filledStyles({
        ...params,
        beforeContent,
      } as filledButtonStylesProps);
      break;
    case 'outlined':
      colorStyles = outlinedStyles(params as outlinedButtonStylesProps);
      break;
    case 'link':
      colorStyles = linkStyles({
        ...params,
        iconColor: colorVariant === 'white' ? colors.primary : undefined,
      } as linkButtonStylesProps);
      break;
  }

  return css`
    &:hover {
      cursor: pointer;
    }
    ${defaultDisabledStyles}
    ${variant === 'outlined' && outlinedButton};
    ${variant === 'link' && underlinedButton}
    ${colorStyles};
  `;
};

export const ButtonContainer = styled(Typography).attrs({
  textAlign: 'center',
  borderRadius: 'none',
})<
  Omit<ButtonProps, 'label'> & {
    constantWidth?: string | null;
    isDisabled?: boolean | null;
  }
>`
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background-color 0.3s ease-in-out,
    color 0.3s ease-in-out,
    border-color 0.3s ease-in-out;

  &::before,
  &::after {
    white-space: pre;
  }
  ${({ beforeContent, size, theme: { lineHeights, space }, variant }) =>
    beforeContent &&
    variant === 'filled' &&
    css`
      &::before {
        position: absolute;
        content: attr(data-text);
        z-index: -1;
        color: transparent;
        line-height: ${lineHeights.p2};
        height: ${switchSize(size, space)};
        padding-inline: ${getPaddingProps(size, space)};
      }
    `}

  ${({ constantWidth, isFluid }) =>
    isFluid
      ? css`
          display: flex;
          width: 100%;
        `
      : css`
          display: inline-flex;
          width: ${constantWidth};
        `}

  ${({
    beforeContent,
    colorVariant,
    isBold,
    isDisabled,
    isProcessing,
    isUpperCase,
    size,
    theme: {
      colors,
      components: { button },
      fontFamilies,
      fontSizes,
      fontWeights,
      space,
    },
    variant,
  }) => css`
    font-size: ${fontSizes.button[size ?? 'm']};
    font-family: ${fontFamilies.openSans};
    height: ${switchSize(size, space)};
    min-width: ${switchSize(size, space)};
    padding-inline: ${getPaddingProps(size, space)};
    gap: ${space.s025};

    ${switchStyles(
      variant,
      colorVariant,
      colors,
      space,
      `${button.borderWidth}px`,
      size,
      beforeContent,
      isDisabled ?? false,
      isProcessing,
    )};

    user-select: none;

    ${isBold &&
    css`
      font-weight: ${fontWeights.bold};
    `}

    ${isUpperCase &&
    css`
      text-transform: uppercase;
    `}

    ${isProcessing &&
    css`
      opacity: 0.6 !important;
      &:hover {
        cursor: wait;
      }
    `};

    @media (max-width: 600px) {
      white-space: normal;
      font-size: 0.75rem;
      padding-inline: 10px;
      line-height: 15px;
    }
  `};
`;

export const InternalLink = styled(LinkWithLocale)`
  text-decoration: none;
`;

export const LinkButtonInnerContent = styled(ButtonContainer)<{
  constantWidth?: string | null;
}>`
  width: ${({ constantWidth }) => (constantWidth ? constantWidth : '100%')};
`;
